// Sign in, the same page in both apps: e-mail and password in one form (each app takes its own kind of account only);
// then, when the API asks for one, the second step — the code from the authenticator app, only for an account that
// turned it on (as in legacy; no code is e-mailed at sign-in) and unless this device was remembered; then a local
// return path, or where the app sends a new session (the cardholder's landing, the dashboard).
import { api, APP, BffError, GENERIC_ERROR, setAccessToken } from '../bff';
import { useBodyClass } from '../dom';
import { fetchSession, useBrand, useSetSession, useTitle, type SessionUser, type SessionView } from '../session';
import { isLocalUrl } from '../url';
import { useState, type FormEvent, type MouseEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PasswordField } from '../PasswordInput';
import { ConsoleShowcase } from './ConsoleShowcase';
import { PaymentShowcase, SPLIT, type ShowcaseKind } from './PaymentShowcase';

const errorText = (err: unknown) => (err instanceof BffError ? err.message : GENERIC_ERROR);

const LOGIN = APP === 'admin' ? '/auth/staff/login' : '/auth/cardholder/login';

type LoginAnswer =
  | { status: 'authenticated'; accessToken: string }
  | { status: 'two_factor'; challengeId: string; method: 'email' | 'totp'; destination: string | null }
  | { status: 'choose_program'; programs: { id: string; displayName: string }[] };

interface Challenge {
  id: string;
  method: 'email' | 'totp';
  destination: string | null;
}

export interface LoginPageProps {
  /** Where a new session goes without a ReturnUrl. */
  landing: (user: SessionUser) => string;
  /** CustomerWeb shows the program's logo; AdminWeb always QOR's. */
  brandLogo: boolean;
  /** The <title> suffix after the brand name (AdminWeb: " Console"). */
  titleSuffix?: string;
  /** The form on glass with a card in flight beside it (instead of the brand picture), speaking for this app. */
  showcase?: ShowcaseKind;
}

export function LoginPage({ landing, brandLogo, titleSuffix = '', showcase }: LoginPageProps) {
  const [params] = useSearchParams();
  const returnUrl = params.get('return');
  const brand = useBrand(null);
  useTitle('Sign In', { displayName: `${brand.displayName}${titleSuffix}` });
  useBodyClass('qor-login');
  const setSession = useSetSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const codeWanted = challenge !== null;
  const [programs, setPrograms] = useState<{ id: string; displayName: string }[] | null>(null);
  const [resent, setResent] = useState(false);
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const info = params.get('reset') === '1' ? 'Your password has been updated. Sign in below.' : null;

  /** A new session: the access token in memory, the session read, then on to the landing page. */
  async function signedIn(accessToken: string) {
    setAccessToken(accessToken);
    const view = await fetchSession();
    setSession(view);
    enter(view);
  }

  async function signIn(e: FormEvent | null, programId?: string) {
    e?.preventDefault();
    setBusy(true);
    try {
      const answer = await api<LoginAnswer>('POST', LOGIN, {
        body: { email, password, ...(programId && { programId }) },
        noRefresh: true,
      });
      setError(null);
      if (answer.status === 'authenticated') return await signedIn(answer.accessToken);
      if (answer.status === 'choose_program') setPrograms(answer.programs);
      else setChallenge({ id: answer.challengeId, method: answer.method, destination: answer.destination });
      setBusy(false);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  const enter = (view: SessionView) =>
    navigate(isLocalUrl(returnUrl) ? returnUrl : view.user ? landing(view.user) : '/', { replace: true });

  async function verifyCode(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true);
    try {
      const answer = await api<{ accessToken: string }>('POST', `${LOGIN}/verify`, {
        body: { challengeId: challenge.id, code: code.replace(/\s/g, ''), rememberDevice },
        noRefresh: true,
      });
      await signedIn(answer.accessToken);
    } catch (err) {
      setError(errorText(err));
      setCode('');
      // a code that is dead (expired, five wrong): back to e-mail and password
      if (
        err instanceof BffError &&
        ['TWO_FACTOR_CHALLENGE_INVALID', 'TWO_FACTOR_TOO_MANY_ATTEMPTS'].includes(err.code)
      ) {
        setChallenge(null);
        setPassword('');
      }
      setBusy(false);
    }
  }

  async function resend(e: MouseEvent) {
    e.preventDefault();
    if (!challenge) return;
    try {
      await api('POST', '/auth/login/resend', { body: { challengeId: challenge.id }, noRefresh: true });
      setResent(true);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }

  /** From the code step back to the form: another account, or the password again. */
  function startOver(e: MouseEvent) {
    e.preventDefault();
    setChallenge(null);
    setPrograms(null);
    setResent(false);
    setCode('');
    setPassword('');
    setError(null);
  }

  return (
    <div className={SPLIT[showcase ?? 'none']}>
      <div className="form-panel">
        <div className="form-inner">
          <Link className="brand" to="/" aria-label={`${brand.displayName} home`}>
            <img src={(brandLogo && brand.logoUrl) || '/images/qor-logo-mark.png'} alt={brand.displayName} />
          </Link>

          <div className="heading">
            <p className="eyebrow">{showcase === 'console' ? 'Staff access' : 'Secure access'}</p>
            <h1>{codeWanted ? 'Enter your code' : programs ? 'Choose your card' : 'Sign in'}</h1>
            {!codeWanted && !programs && (
              <p>{showcase === 'console' ? 'Welcome back to the console.' : 'Your card. Always within reach.'}</p>
            )}
          </div>

          {error ? (
            <div className="alert alert-error" role="alert">
              {error}
            </div>
          ) : (
            info && (
              <div className="alert alert-success" role="status">
                {info}
              </div>
            )
          )}

          {codeWanted ? (
            <>
              <div className="email-summary">
                <span>{email}</span>
                <a className="link" href="/login" onClick={startOver}>
                  Change
                </a>
              </div>
              <form onSubmit={(e) => void verifyCode(e)}>
                <label className="field">
                  <span>Authentication code</span>
                  <input
                    name="TwoFactorCode"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder={
                      challenge?.method === 'email'
                        ? `6-digit code sent to ${challenge.destination ?? 'your e-mail'}`
                        : '6-digit code from your authenticator app'
                    }
                    maxLength={7}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    autoFocus
                  />
                </label>
                <label className="check">
                  <input
                    type="checkbox"
                    name="RememberDevice"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                  />
                  <span>Remember this device for 30 days</span>
                </label>
                <button type="submit" className="submit" disabled={busy}>
                  Verify
                </button>
              </form>
              {challenge?.method === 'email' ? (
                <p className="hint">
                  {resent ? 'A new code is on its way. ' : "Didn't get it? "}
                  <a className="link" href="/login" onClick={(e) => void resend(e)}>
                    Send a new code
                  </a>
                </p>
              ) : (
                <p className="hint">Lost your device? Contact support to reset two-factor authentication.</p>
              )}
            </>
          ) : programs ? (
            <>
              <div className="email-summary">
                <span>{email}</span>
                <a className="link" href="/login" onClick={startOver}>
                  Change
                </a>
              </div>
              <p className="note">You have a card with more than one program. Which one do you want to open?</p>
              {programs.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="submit"
                  disabled={busy}
                  onClick={() => void signIn(null, p.id)}
                  style={{ marginBottom: 10 }}
                >
                  {p.displayName}
                </button>
              ))}
            </>
          ) : (
            <>
              <form onSubmit={(e) => void signIn(e)}>
                <label className="field">
                  <span>Email</span>
                  <input
                    type="email"
                    name="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    autoComplete="username"
                    required
                    autoFocus
                  />
                </label>
                <PasswordField
                  className="field"
                  label="Password"
                  name="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  autoComplete="current-password"
                  required
                />
                <button type="submit" className="submit" disabled={busy}>
                  Sign In
                </button>
              </form>

              <p className="hint">
                <Link
                  className="link"
                  to={email.trim() ? `/forgot-password?email=${encodeURIComponent(email.trim())}` : '/forgot-password'}
                >
                  Forgot password?
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
      {showcase === 'console' ? (
        <ConsoleShowcase />
      ) : showcase ? (
        <PaymentShowcase kind={showcase} />
      ) : (
        <div className="hero-panel">
          <img
            className="hero-logo"
            src="/images/qor-logo-overlay.png"
            alt={`${brand.displayName} - The future is here`}
          />
        </div>
      )}
    </div>
  );
}
